// The Start-outreach card from the chat component gallery: megaphone chip,
// title + state + one-line scope, and a right-hand slot that is either the
// start action or a link to the outreach once it exists. The megaphone and
// its sound waves are the gallery's exact paths because the broadcast ripple
// animates those two wave strokes (keyframes live in global.css).
import { ArrowUpRight } from 'lucide-react';
import { type ReactNode } from 'react';

import { CompactReceipt } from '@apps/web/features/phoebe_chat_v3/ui/primitives/compact_receipt';
import { ResolvedReferenceText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';
import { cn } from '@apps/web/lib/utils';

export type OutreachCardProps = Readonly<{
    readonly title: string;
    readonly detail: string;
    readonly stateLabel?: string;
    /** Plays the two-cycle megaphone ripple while true. */
    readonly broadcasting?: boolean;
    /** Purple receipt border once the outreach exists. */
    readonly receipt?: boolean;
    /** Label and callback travel together so a caller cannot render a
        primary action that silently does nothing. */
    readonly action?: Readonly<{
        readonly label: string;
        readonly onAction: () => void;
    }>;
    /** Where the arrow goes once the outreach exists. */
    readonly viewHref?: string;
    readonly disabled?: boolean;
}>;

export function OutreachCard({
    title,
    detail,
    stateLabel,
    broadcasting = false,
    receipt = false,
    action,
    viewHref,
    disabled = false,
}: OutreachCardProps): ReactNode {
    if (receipt || stateLabel === 'Denied') {
        return (
            <CompactReceipt
                surface="outreach"
                title={title}
                status={stateLabel === 'Denied' ? 'denied' : 'running'}
                summary={`${detail}${receipt ? ' · No one contacted yet' : ''}`}
                action={
                    viewHref === undefined ? undefined : (
                        <a
                            aria-label="View outreach"
                            href={disabled ? undefined : viewHref}
                            aria-disabled={disabled || undefined}
                            className="text-agent-action focus-visible:ring-ring/50 text-v3-chat rounded-md px-2 py-1 font-semibold outline-none focus-visible:ring-[3px]"
                        >
                            View outreach{' '}
                            <ArrowUpRight
                                aria-hidden={true}
                                className="ml-1 inline size-4"
                            />
                        </a>
                    )
                }
            />
        );
    }
    return (
        <article
            className={cn(
                '@container/outreach-card bg-card grid min-w-0 origin-bottom',
                'grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2.5',
                'rounded-lg border',
                'px-[11px] py-2.5 shadow-[0_1px_3px_rgba(0,0,0,0.07)]',
                receipt && 'border-agent-action-border',
                broadcasting && 'phoebe-outreach-broadcasting',
            )}
        >
            <span className="bg-agent-action-wash text-agent-action relative inline-flex size-8 shrink-0 items-center justify-center rounded-md dark:text-violet-300">
                <MegaphoneGlyph />
                <SoundWaves />
            </span>
            <div className="min-w-0">
                <div className="flex min-w-0 items-baseline gap-[7px]">
                    <strong className="text-v3-chat truncate font-bold">
                        <ResolvedReferenceText text={title} />
                    </strong>
                    {stateLabel !== undefined && (
                        <span className="text-muted-foreground text-v3-chat-meta shrink-0 font-semibold">
                            <ResolvedReferenceText text={stateLabel} />
                        </span>
                    )}
                </div>
                <p className="text-muted-foreground text-v3-chat-meta mt-0.5 truncate">
                    <ResolvedReferenceText text={detail} />
                    {receipt && ' · Preparing · No one contacted yet'}
                </p>
            </div>
            {action !== undefined ? (
                <button
                    type="button"
                    disabled={disabled}
                    onClick={action.onAction}
                    className="border-agent-action bg-agent-action hover:border-agent-action-strong hover:bg-agent-action-strong text-v3-chat col-span-3 min-h-[33px] w-full rounded-md border px-3 py-[7px] font-semibold text-white disabled:opacity-[0.42] @min-[360px]/outreach-card:col-span-1 @min-[360px]/outreach-card:w-auto"
                >
                    <ResolvedReferenceText text={action.label} />
                </button>
            ) : viewHref !== undefined ? (
                <a
                    href={viewHref}
                    aria-label="View outreach"
                    className="text-agent-action hover:bg-agent-action-wash inline-flex size-[30px] items-center justify-center self-center rounded-md dark:text-violet-300"
                >
                    <ArrowUpRight aria-hidden="true" className="size-4" />
                </a>
            ) : null}
        </article>
    );
}

function MegaphoneGlyph(): ReactNode {
    return (
        <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            className="phoebe-megaphone-glyph size-4"
        >
            <path
                d="m3 11 18-5v12L3 14v-3Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M11.6 16.8a3 3 0 1 1-5.8-1.6"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function SoundWaves(): ReactNode {
    const waveClassName = cn(
        'fill-none stroke-current opacity-0 [stroke-linecap:round]',
        '[stroke-width:1.7] [transform-box:fill-box] origin-left',
    );
    return (
        <svg
            viewBox="0 0 12 20"
            aria-hidden="true"
            className="text-agent-action pointer-events-none absolute top-1.5 left-[23px] h-5 w-3 overflow-visible dark:text-violet-300"
        >
            <path
                className={cn(waveClassName, 'phoebe-outreach-wave-near')}
                d="M1.5 7q3 3 0 6"
            />
            <path
                className={cn(waveClassName, 'phoebe-outreach-wave-far')}
                d="M4 3.5q6.5 6.5 0 13"
            />
        </svg>
    );
}
